import React, { useState, useEffect, useMemo } from "react";
import { fetchProducts, updateProduct, createProduct, deleteProduct, resolveImg } from "../../services/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";
import { useToast } from "../../hooks/use-toast";
import ImageUploader from "./ImageUploader";
import { Loader2, Pencil, Trash2, Plus, Search, ImagePlus, X } from "lucide-react";

const CATS = [
  { slug: "medals-awards", name: "Medals & Awards" },
  { slug: "running-marathon", name: "Running & Marathon" },
  { slug: "teamwear", name: "Teamwear" },
  { slug: "promotional-products", name: "Promotional Products" },
];

const empty = { name: "", category: "medals-awards", subcategory: "", image: "", images: [], material: "", moq: "No MOQ", leadTime: "10-15 days", tags: [], description: "", features: [] };

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // product object or 'new'
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const { toast } = useToast();

  const load = () => {
    setLoading(true);
    fetchProducts().then(setProducts).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    return products.filter((p) =>
      (cat === "all" || p.category === cat) &&
      p.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [products, cat, search]);

  const openEdit = (p) => { setEditing(p); setForm({ ...empty, ...p, tags: p.tags || [], images: p.images || [], features: p.features || [] }); };
  const openNew = () => { setEditing("new"); setForm(empty); };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name) { toast({ title: "Name is required", variant: "destructive" }); return; }
    setSaving(true);
    try {
      if (editing === "new") {
        await createProduct(form);
        toast({ title: "Product created" });
      } else {
        await updateProduct(editing.id, form);
        toast({ title: "Product updated" });
      }
      setEditing(null);
      load();
    } catch (e) {
      toast({ title: "Save failed", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await deleteProduct(delTarget.id);
      toast({ title: "Product deleted" });
      setDelTarget(null);
      load();
    } catch (e) { toast({ title: "Delete failed", variant: "destructive" }); }
  };

  const addImage = () => set("images", [...(form.images || []), ""]);
  const setImageAt = (i, v) => set("images", form.images.map((x, idx) => (idx === i ? v : x)));
  const removeImageAt = (i) => set("images", form.images.filter((_, idx) => idx !== i));

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products" className="pl-9 w-56" />
          </div>
          <select value={cat} onChange={(e) => setCat(e.target.value)} className="h-10 rounded-md border border-neutral-200 px-3 text-sm">
            <option value="all">All categories</option>
            {CATS.map((c) => (<option key={c.slug} value={c.slug}>{c.name}</option>))}
          </select>
        </div>
        <Button onClick={openNew} className="bg-[#C9A227] hover:bg-[#A6841B] text-white"><Plus className="w-4 h-4 mr-1" /> New Product</Button>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 text-[#C9A227] animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((p) => (
            <div key={p.id} className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
              <div className="aspect-square bg-neutral-100 overflow-hidden"><img src={resolveImg(p.image)} alt={p.name} className="w-full h-full object-cover" /></div>
              <div className="p-3">
                <div className="text-[10px] uppercase tracking-wider text-[#C9A227] font-bold">{p.subcategory}</div>
                <div className="font-semibold text-sm text-[#14171c] line-clamp-1">{p.name}</div>
                {(p.images?.length > 0) && <div className="text-xs text-neutral-400 mt-0.5">+{p.images.length} extra image(s)</div>}
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)} className="flex-1 h-8 text-xs"><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => setDelTarget(p)} className="h-8 w-8 p-0 text-red-500 hover:text-red-600 border-red-200"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit/Create dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing === "new" ? "New Product" : "Edit Product"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input className="mt-1.5" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <select value={form.category} onChange={(e) => set("category", e.target.value)} className="mt-1.5 w-full h-10 rounded-md border border-neutral-200 px-3 text-sm">
                  {CATS.map((c) => (<option key={c.slug} value={c.slug}>{c.name}</option>))}
                </select>
              </div>
              <div><Label>Subcategory</Label><Input className="mt-1.5" value={form.subcategory} onChange={(e) => set("subcategory", e.target.value)} /></div>
            </div>
            <ImageUploader label="Main Image" value={form.image} onChange={(v) => set("image", v)} />
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label>Additional Images</Label>
                <button type="button" onClick={addImage} className="inline-flex items-center gap-1 text-sm font-semibold text-[#C9A227]"><ImagePlus className="w-4 h-4" /> Add image</button>
              </div>
              <div className="space-y-3">
                {(form.images || []).map((img, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="flex-1"><ImageUploader label="" value={img} onChange={(v) => setImageAt(i, v)} /></div>
                    <button type="button" onClick={() => removeImageAt(i)} className="mt-1 text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                  </div>
                ))}
                {(!form.images || form.images.length === 0) && <p className="text-xs text-neutral-400">No additional images yet.</p>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Material</Label><Input className="mt-1.5" value={form.material} onChange={(e) => set("material", e.target.value)} /></div>
              <div><Label>Tags (comma separated)</Label><Input className="mt-1.5" value={(form.tags || []).join(", ")} onChange={(e) => set("tags", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} /></div>
              <div><Label>MOQ</Label><Input className="mt-1.5" value={form.moq} onChange={(e) => set("moq", e.target.value)} /></div>
              <div><Label>Lead Time</Label><Input className="mt-1.5" value={form.leadTime} onChange={(e) => set("leadTime", e.target.value)} /></div>
            </div>
            <div><Label>Description</Label><Textarea className="mt-1.5" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
            <div><Label>Features (one per line)</Label><Textarea className="mt-1.5" rows={3} value={(form.features || []).join("\n")} onChange={(e) => set("features", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#C9A227] hover:bg-[#A6841B] text-white">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete product?</AlertDialogTitle><AlertDialogDescription>This will permanently remove "{delTarget?.name}".</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminProducts;
